import { useEffect, useMemo, useState } from "react";
import { BarChart3, Trophy, Medal, Users, CalendarCheck, AlertTriangle, Loader2, Flame } from "lucide-react";
import { toast } from "sonner";
import AppShell, { useAppUser, ROLE_LABELS } from "@/components/AppShell";
import BeltBar from "@/components/BeltBar";
import { authApi, formatApiError } from "@/lib/api";

const MEDAL = ["text-gold", "text-zinc-300", "text-gold-amber"];
const ROLE_PRIORITY = ["admin", "responsavel", "instrutor", "lideranca", "financeiro", "aluno"];
const PROFILE_FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "aluno", label: "Alunos" },
  { key: "instrutor", label: "Instrutores" },
  { key: "responsavel", label: "Resp. Técnico" },
  { key: "lideranca", label: "Liderança" },
  { key: "financeiro", label: "Financeiro" },
];
const mainRole = (roles) => ROLE_PRIORITY.find((r) => (roles || []).includes(r)) || "aluno";

export default function Frequencia() {
  const ctx = useAppUser() || {};
  const roles = ctx.user ? ctx.user.roles || [ctx.user.role] : [];
  const isFull = roles.includes("admin") || roles.includes("responsavel");

  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [perfil, setPerfil] = useState("todos");

  useEffect(() => {
    authApi().get("/units").then((r) => {
      const list = r.data?.units || r.data || [];
      setUnits(list);
      if (list.length && !unitId) setUnitId(list[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleUnits = useMemo(() => {
    if (isFull) return units;
    const myEmail = ctx.user?.email;
    return units.filter((u) => (u.instructors || []).some((i) => i.email === myEmail));
  }, [units, isFull, ctx.user]);

  useEffect(() => {
    if (!unitId) return;
    setLoading(true);
    authApi().get("/frequencia/kpis", { params: { unit_id: unitId } })
      .then((r) => setData(r.data))
      .catch((err) => {
        setData(null);
        toast.error(formatApiError(err.response?.data?.detail));
      })
      .finally(() => setLoading(false));
  }, [unitId]);

  useEffect(() => {
    if (visibleUnits.length && !visibleUnits.some((u) => u.id === unitId)) setUnitId(visibleUnits[0].id);
  }, [visibleUnits, unitId]);

  const filtered = useMemo(
    () => (data?.ranking || []).filter((r) => perfil === "todos" || (r.roles || []).includes(perfil)),
    [data, perfil]
  );
  const podium = filtered.slice(0, 3);
  const resto = filtered.slice(3);

  return (
    <AppShell active="frequencia">
      <div className="relative" data-testid="frequencia-page">
        <div className="absolute inset-0 flex items-start justify-center pt-24 pointer-events-none overflow-hidden">
          <img src="/assets/logo.jpeg" alt="" className="w-[45%] max-w-md opacity-[0.05] rounded-full" />
        </div>

        <div className="relative">
          <p className="font-mono text-[10px] tracking-[0.3em] text-gold">FREQUÊNCIA</p>
          <h1 className="mt-1 font-display text-2xl font-bold flex items-center gap-3" data-testid="frequencia-title">
            <BarChart3 size={22} className="text-gold" /> Ranking de Presença
          </h1>
          <p className="mt-1 text-sm text-zinc-400">{data ? `${data.unit?.name} · ${data.semestre}` : "Acompanhe a frequência e o ranking do semestre."}</p>

          <div className="mt-5">
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">UNIDADE</label>
            <select value={unitId} onChange={(e) => setUnitId(e.target.value)} data-testid="frequencia-unit-select"
              className="mt-1 w-full sm:w-80 bg-obsidian border border-white/15 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] focus:border-gold/60 outline-none">
              {visibleUnits.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>

          <div className="mt-4 flex gap-2 flex-wrap" data-testid="frequencia-profile-filters">
            {PROFILE_FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setPerfil(f.key)} data-testid={`frequencia-filter-${f.key}`}
                className={`font-mono text-[10px] tracking-widest px-4 py-2 rounded-full border transition-colors ${perfil === f.key ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-400 hover:border-gold/40"}`}>
                {f.label.toUpperCase()}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="mt-10 text-sm text-zinc-500 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Calculando ranking...</p>
          ) : !data ? null : data.ranking.length === 0 ? (
            <div className="mt-10 flex flex-col items-center gap-3 border border-dashed border-white/15 rounded-2xl py-16 text-zinc-500" data-testid="frequencia-empty">
              <Users size={28} />
              <p className="text-sm">Nenhum aluno vinculado a esta unidade.</p>
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="frequencia-kpis">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-1.5"><CalendarCheck size={12} /> AULAS NO SEMESTRE</p>
                  <p className="mt-1 font-display text-3xl font-bold text-gold" data-testid="kpi-aulas">{data.total_aulas}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-1.5"><Users size={12} /> ALUNOS</p>
                  <p className="mt-1 font-display text-3xl font-bold" data-testid="kpi-alunos">{data.ranking.length}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-1.5"><Trophy size={12} /> MÉDIA POR AULA</p>
                  <p className="mt-1 font-display text-3xl font-bold" data-testid="kpi-media">{data.media_presencas_por_aula}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="font-mono text-[10px] tracking-widest text-zinc-500 flex items-center gap-1.5"><AlertTriangle size={12} /> AUSÊNCIAS FREQUENTES</p>
                  <p className={`mt-1 font-display text-3xl font-bold ${data.ausencias_frequentes ? "text-orange-400" : ""}`} data-testid="kpi-ausencias">{data.ausencias_frequentes}</p>
                </div>
              </div>

              {podium.length > 0 && (
                <div className="mt-8 rounded-2xl border border-gold/25 bg-gold/[0.04] p-5" data-testid="frequencia-podium">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-gold mb-4 flex items-center gap-2"><Trophy size={13} /> PÓDIO DO SEMESTRE</p>
                  <div className="space-y-2">
                    {podium.map((r, i) => (
                      <div key={r.email} className="flex items-center gap-3 rounded-xl border border-white/10 bg-obsidian px-4 py-3" data-testid={`podium-${i}`}>
                        <Medal size={20} className={MEDAL[i]} />
                        <span className="font-display font-bold text-lg w-8 text-zinc-500">#{r.posicao}</span>
                        <span className="flex-1 min-w-0">
                          <span className="font-medium text-sm truncate block">{r.name}</span>
                          <span className="flex items-center gap-2 mt-0.5">
                            <BeltBar belt={r.belt} degree={r.degree} size="sm" />
                            <span className="font-mono text-[9px] tracking-wider text-zinc-500 uppercase">{(ROLE_LABELS[mainRole(r.roles)] || "").toUpperCase()}</span>
                          </span>
                        </span>
                        <span className="text-right shrink-0">
                          <span className="font-display font-bold text-gold">{r.presencas}</span>
                          <span className="font-mono text-[10px] text-zinc-500 block">{r.percentual}% DAS AULAS</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 space-y-2" data-testid="frequencia-ranking">
                {filtered.length === 0 && (
                  <p className="text-sm text-zinc-500 border border-dashed border-white/15 rounded-xl py-8 text-center" data-testid="frequencia-filter-empty">
                    Nenhum membro com este perfil nesta unidade.
                  </p>
                )}
                {resto.map((r) => (
                  <div key={r.email} className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${r.ausencia_pendente ? "border-orange-500/40 bg-orange-500/[0.05]" : "border-white/10 bg-white/[0.02]"}`} data-testid={`rank-row-${r.posicao}`}>
                    <span className="font-display font-bold text-zinc-500 w-8">#{r.posicao}</span>
                    <span className="flex-1 min-w-0">
                      <span className="font-medium text-sm truncate block">{r.name}</span>
                      <span className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <BeltBar belt={r.belt} degree={r.degree} size="sm" />
                        <span className="font-mono text-[9px] tracking-wider text-zinc-500 uppercase">{(ROLE_LABELS[mainRole(r.roles)] || "").toUpperCase()}</span>
                        {r.ausencia_pendente && (
                          <span className="font-mono text-[9px] tracking-wider px-2 py-0.5 rounded-full border border-orange-500/60 text-orange-400 inline-flex items-center gap-1">
                            <Flame size={9} /> {r.faltas_seguidas} FALTAS SEGUIDAS
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="text-right shrink-0">
                      <span className="font-display font-bold">{r.presencas}</span>
                      <span className="font-mono text-[10px] text-zinc-500 block">{r.percentual}%</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
